import { closureRate, mean, percentage, variancePoints } from './ratio.js';
/**
 * App 1 — QA/QC derived fields (spec §9).
 *
 * ADR-0007 in practice. Every function here is pure, takes its configuration as a
 * parameter rather than reading it, and returns the `explanation` alongside the
 * value so the tooltip, the dashboard and the Excel export all say the same thing
 * about the same number.
 *
 * Nothing in this file accepts a pre-computed value. The API recomputes on every
 * write and the UI recomputes for display, both from here — which is what stops a
 * dashboard and a report of the same period disagreeing.
 */
export const QAQC_METRIC_CATEGORIES = [
    'external_ncr',
    'internal_ncr',
    'rfi',
    'rmi',
];
export const QAQC_METRIC_CATEGORY_LABELS = {
    external_ncr: 'External NCR',
    internal_ncr: 'Internal NCR',
    rfi: 'RFI',
    rmi: 'RMI',
};
/**
 * A stored weight set's weights, keyed by metric category.
 *
 * The Master Data form and its API name the weights `externalNcr`,
 * `internalNcr`, `rfi` and `materialInspection`; the calculation keys them by
 * category. Sets saved on screen were therefore read as all-zero, and every
 * PQI fell back to an equal split — whatever the organisation had configured.
 * Both spellings are read here, so sets already saved need no rewriting
 * (ADR-0018). A weight not given is zero.
 */
const PQI_WEIGHT_ALIASES = {
    externalNcr: 'external_ncr',
    internalNcr: 'internal_ncr',
    materialInspection: 'rmi',
};
export function pqiWeightsFrom(stored) {
    const weights = Object.fromEntries(QAQC_METRIC_CATEGORIES.map((category) => [category, 0]));
    if (!stored || typeof stored !== 'object')
        return weights;
    for (const [key, value] of Object.entries(stored)) {
        const category = PQI_WEIGHT_ALIASES[key] ?? QAQC_METRIC_CATEGORIES.find((each) => each === key);
        if (category && typeof value === 'number' && Number.isFinite(value))
            weights[category] = value;
    }
    return weights;
}
/**
 * Did anything at all happen in this category this period?
 *
 * This is the gate that decides whether a category contributes to the PQI. The
 * test is deliberately strict — nothing issued, nothing closed, and nothing
 * sitting in any ageing bucket. A category with open items carried over from a
 * previous month HAS activity even if nothing new was issued, and excluding it
 * would flatter the index by hiding the ageing it is carrying.
 *
 * ASSUMPTION, flagged for Algihaz (Rule 6): the BRD does not define "no activity".
 * If they mean "nothing newly issued", this single predicate is where it changes.
 */
export function categoryHasActivity(counts) {
    return (counts.issuedCount > 0 ||
        counts.closedCount > 0 ||
        counts.ageing0To15 > 0 ||
        counts.ageing15To45 > 0 ||
        counts.ageingOver45 > 0);
}
/** Total open items across the three ageing buckets. */
function openItems(counts) {
    return counts.ageing0To15 + counts.ageing15To45 + counts.ageingOver45;
}
/**
 * The shipped defaults.
 *
 * These numbers are ours, not Algihaz's — spec §9 requires a composite index and
 * never defines one. Equal weighting is the least-assuming starting point, and
 * `isProvisional` keeps that visible on every screen rather than letting an
 * invented figure quietly acquire authority.
 */
export const DEFAULT_PQI_WEIGHT_SET = {
    weights: { external_ncr: 0.25, internal_ncr: 0.25, rfi: 0.25, rmi: 0.25 },
    ageingPenaltyPointsPerPct: 0.5,
    ageingPenaltyCap: 25,
    isProvisional: true,
};
// ---------------------------------------------------------------------------
// Derived fields
// ---------------------------------------------------------------------------
/**
 * Closure rate for one category. Zero-denominator rule: **100% when nothing was
 * issued** — the BRD's own worked example (spec §5.1), not an assumption.
 */
export function categoryClosureRate(counts) {
    return closureRate(counts.issuedCount, counts.closedCount);
}
/**
 * The ageing penalty, in points, deducted from a category's closure rate.
 *
 * Zero-denominator rule: **0 points when there are no open items at all**. Nothing
 * is ageing, so nothing is penalised — an explicit value, not a fallback.
 */
export function ageingPenalty(counts, config) {
    const open = openItems(counts);
    const pctOver45 = percentage(counts.ageingOver45, open, { kind: 'value', value: 0 });
    if (pctOver45.value === null || pctOver45.value === 0) {
        return {
            value: 0,
            fromZeroDenominatorRule: pctOver45.fromZeroDenominatorRule,
            explanation: open === 0
                ? 'No open items, so no ageing penalty applies.'
                : 'No open items aged over 45 days.',
        };
    }
    const raw = pctOver45.value * config.ageingPenaltyPointsPerPct;
    const capped = Math.min(raw, config.ageingPenaltyCap);
    return {
        value: Math.round(capped * 100) / 100,
        fromZeroDenominatorRule: false,
        explanation: capped < raw
            ? `${pctOver45.value}% of open items are over 45 days; penalty capped at ${config.ageingPenaltyCap} points.`
            : `${pctOver45.value}% of open items are over 45 days.`,
    };
}
/**
 * A category's contribution to the PQI: closure rate less the ageing penalty,
 * clamped to 0–100.
 *
 * ASSUMPTION, flagged for Algihaz (Rule 6): spec §9 gives no formula. This shape —
 * "how much did you close, less how much is going stale" — is ours. The numbers
 * are configuration; if the *shape* is wrong, this function is where it changes.
 */
export function categoryScore(category, counts, config) {
    const rate = categoryClosureRate(counts);
    const penalty = ageingPenalty(counts, config);
    if (!categoryHasActivity(counts)) {
        return {
            category,
            active: false,
            value: null,
            closureRate: rate,
            penalty,
            explanation: 'No activity in this category this period, so it is excluded from the PQI.',
        };
    }
    const raw = (rate.value ?? 0) - (penalty.value ?? 0);
    const clamped = Math.max(0, Math.min(100, raw));
    return {
        category,
        active: true,
        value: Math.round(clamped * 100) / 100,
        closureRate: rate,
        penalty,
        explanation: `Closure rate ${rate.value}% less an ageing penalty of ${penalty.value} points.`,
    };
}
/**
 * Project Quality Index — the weighted mean of the ACTIVE categories' scores.
 *
 * Zero-denominator rule: **100 when no category had any activity**. Consistent
 * with the BRD's closure-rate rule — nothing was outstanding, so nothing was left
 * unclosed. Not zero, which would read as total failure.
 *
 * Inactive categories are excluded and the remaining weights re-normalise, so a
 * project that simply does not raise RMIs is not dragged down by an empty
 * category. The invariant that matters: an inactive category's weight has no
 * effect on the result at all.
 */
export function projectQualityIndex(countsByCategory, config = DEFAULT_PQI_WEIGHT_SET) {
    const empty = {
        issuedCount: 0,
        closedCount: 0,
        ageing0To15: 0,
        ageing15To45: 0,
        ageingOver45: 0,
    };
    const categories = QAQC_METRIC_CATEGORIES.map((category) => categoryScore(category, countsByCategory[category] ?? empty, config));
    const active = categories.filter((score) => score.active);
    if (active.length === 0) {
        return {
            value: 100,
            fromZeroDenominatorRule: true,
            explanation: 'No QA/QC activity was recorded in this period, so nothing was outstanding.',
            categories,
            appliedWeights: {},
            isProvisional: config.isProvisional,
        };
    }
    const weightTotal = active.reduce((sum, score) => sum + (config.weights[score.category] ?? 0), 0);
    // A weight set that gives every active category a weight of zero would divide by
    // zero. Fall back to an equal split rather than erroring — the configuration is
    // wrong, but a report must still produce a number, and the explanation says so.
    const useEqualSplit = weightTotal <= 0;
    const appliedWeights = {};
    let weighted = 0;
    for (const score of active) {
        const weight = useEqualSplit ? 1 / active.length : (config.weights[score.category] ?? 0) / weightTotal;
        appliedWeights[score.category] = Math.round(weight * 10000) / 10000;
        weighted += weight * (score.value ?? 0);
    }
    const excluded = categories.filter((score) => !score.active);
    return {
        value: Math.round(weighted * 100) / 100,
        fromZeroDenominatorRule: false,
        explanation: useEqualSplit
            ? 'Weights for the active categories sum to zero, so an equal split was applied. Check the weighting configuration.'
            : excluded.length > 0
                ? `Weighted across ${active.length} active ${active.length === 1 ? 'category' : 'categories'}; ${excluded
                    .map((score) => QAQC_METRIC_CATEGORY_LABELS[score.category])
                    .join(', ')} had no activity and ${excluded.length === 1 ? 'was' : 'were'} excluded.`
                : `Weighted across all ${active.length} categories.`,
        categories,
        appliedWeights,
        isProvisional: config.isProvisional,
    };
}
/** Month-over-month PQI movement in points. Null when there is nothing to compare. */
export function pqiVariance(current, previous) {
    return variancePoints(current, previous);
}
// ---------------------------------------------------------------------------
// The remaining App 1 derived fields
// ---------------------------------------------------------------------------
/**
 * Average attendance per toolbox talk.
 *
 * Zero-denominator rule: **not applicable (null)** when no sessions were held.
 * Zero sessions is not zero attendance — it is no data, and it renders as an em
 * dash rather than a number that invites comparison.
 */
export function qtbtAverageAttendance(sessionCount, attendeeCount) {
    return mean(attendeeCount, sessionCount, { kind: 'not_applicable' });
}
/**
 * Accumulated year-to-date totals.
 *
 * Zero-denominator rule: **0**, stated explicitly. A sum of nothing genuinely is
 * zero, unlike an average of nothing — the two differ deliberately, and saying so
 * here is what stops someone "fixing" one to match the other.
 */
export function qtbtAccumulatedTotals(periods) {
    const totals = periods.reduce((acc, period) => ({
        sessions: acc.sessions + period.sessionCount,
        attendees: acc.attendees + period.attendeeCount,
        durationMinutes: acc.durationMinutes + period.totalDurationMinutes,
    }), { sessions: 0, attendees: 0, durationMinutes: 0 });
    return {
        ...totals,
        explanation: periods.length === 0
            ? 'No periods recorded yet, so the accumulated totals are zero.'
            : `Accumulated across ${periods.length} period(s).`,
    };
}
/**
 * Material inspection: the status breakdown must reconcile to the entered total
 * (spec §9).
 *
 * Zero case: **0 vs 0 reconciles**. An empty form is not a failed form — the check
 * only bites at submit, so a half-entered draft stays savable.
 *
 * ASSUMPTION, flagged (Rule 6): OSD counts (overage/shortage/damage) are a
 * separate dimension and are NOT part of this total. Including them would reject
 * valid data.
 */
export function reconcileMaterialInspection(counts) {
    const statusTotal = counts.statusApproved +
        counts.statusOnHold +
        counts.statusRejected +
        counts.statusHazardous +
        counts.statusHandleWithCare;
    const difference = statusTotal - counts.mirnTotal;
    if (difference === 0) {
        return {
            reconciles: true,
            statusTotal,
            mirnTotal: counts.mirnTotal,
            difference: 0,
            explanation: counts.mirnTotal === 0
                ? 'No material inspections recorded this period.'
                : 'The status breakdown reconciles to the MIRN total.',
        };
    }
    return {
        reconciles: false,
        statusTotal,
        mirnTotal: counts.mirnTotal,
        difference,
        explanation: difference > 0
            ? `The status breakdown totals ${statusTotal}, which is ${difference} more than the MIRN total of ${counts.mirnTotal}.`
            : `The status breakdown totals ${statusTotal}, which is ${Math.abs(difference)} fewer than the MIRN total of ${counts.mirnTotal}.`,
    };
}
/**
 * Average document review time.
 *
 * Zero-denominator rule: **not applicable (null)** when nothing was reviewed. No
 * completed reviews means no average — not a zero-day turnaround, which would
 * read as exemplary performance.
 */
export function averageReviewDays(reviewDaysTotal, reviewedCount) {
    return mean(reviewDaysTotal, reviewedCount, { kind: 'not_applicable' });
}
export const CSAT_DIMENSIONS = [
    'ratingQuality',
    'ratingTimeliness',
    'ratingCommunication',
    'ratingResponsiveness',
    'ratingDocumentation',
    'ratingOverall',
];
/**
 * Customer satisfaction averages, per dimension and overall.
 *
 * Zero-denominator rule: **not applicable (null)** per dimension when nobody
 * rated it, and overall when no dimension has a value.
 *
 * A blank dimension shrinks only THAT dimension's denominator — counting a skipped
 * question as a zero would punish the project for a question nobody answered.
 */
export function csatAverages(responses) {
    const byDimension = {};
    for (const dimension of CSAT_DIMENSIONS) {
        const rated = responses
            .map((response) => response[dimension])
            .filter((rating) => typeof rating === 'number');
        byDimension[dimension] = mean(rated.reduce((sum, rating) => sum + rating, 0), rated.length, { kind: 'not_applicable' });
    }
    const dimensionValues = CSAT_DIMENSIONS.map((dimension) => byDimension[dimension].value).filter((value) => value !== null);
    return {
        byDimension,
        overall: mean(dimensionValues.reduce((sum, value) => sum + value, 0), dimensionValues.length, { kind: 'not_applicable' }),
    };
}
/** Closure-rate month-over-month variance, re-exported for symmetry. */
export { variancePoints as closureRateVariance };
//# sourceMappingURL=qaqc.js.map